// StoreBox.tsx
import React, { useEffect, useState } from "react";
import {MapPin , StarIcon } from "lucide-react"
import { useTranslation } from "react-i18next";
import { StoreAdmin } from "../../../types/modules/admin/StoreAdmin.type";
import { StoreStatus } from "../../../types/enum/storeStatus";
import { fetchAddressFromCoords } from "../../../utils/methods/fetchAddressFromCoords";
import { useUpdateResource } from "../../../hooks/useUpdateResource";
import { Link } from "react-router-dom";
import { formatNumber, formatTime } from "../../../utils/formatNumber";
import { CustomDate } from "../../../utils/day";
import { PermissionGate } from "../PermissionGate";
import { PermissionsKey } from "../../../data/permissions";


interface Props {
  store: StoreAdmin;
  status?:StoreStatus
}

const StoreAdminBox: React.FC<Props> = ({ store,status}) => {
  const isOpen = store.openTime && store.closeTime; // بس مثال لتحديد مفتوح/مغلق

  // جلب اسم المتجر بالعربي كمثال
  const storeName = store.languages.find(l => l.languageCode === "ar")?.name || store.languages[0].name;

  const subTypeName = store.subType.languages[0].name
  const parentTypeName = store.subType.type.languages[0].name;

  const {t} = useTranslation();

    const [address, setAddress] = useState("");
  
  useEffect(() => {
    const getAddress = async () => {
      const result = await fetchAddressFromCoords(store.lat, store.lng);
      setAddress(result);
    };
    getAddress();
  }, [store.lat, store.lng]);

  
      const {updateResource} = useUpdateResource();
      async function handleApprove(){
          await  updateResource(`store/${store.id}/approved`, {},"admin",{
              method: "PUT",
              successMessage: t("approve_success"),
              invalidateQueries:["stores-by-admin"],
              confirmMessage:t("confirm_approve")
          });
      }

      async function handleReject(){
          await  updateResource(`store/${store.id}/rejected`, {},"admin",{
              method: "PUT",
              successMessage: t("reject_success"),
              invalidateQueries:["stores-by-admin"],
              confirmMessage:t("confirm_reject")
          });
      }


  return (
    <div className="rounded-[20px] bg-[#FFFFFF] overflow-hidden shadow">
      {/* Cover Image */}
      <div className="relative w-full h-32">
            {store.coverUrl?
                <img src={store.coverUrl} alt="Cover" className="w-full h-full object-cover" />
                :
                <div className="w-full h-full bg-gradient-to-r from-primary/50 to-secondary/50 flex items-center justify-center text-light font-bold text-xl">
                No Cover Image
              </div>
            }
            
          <div className="absolute inset-0 bg-black/25"></div>


        {/* Top Right: City */}
        <span 
        className="absolute top-2 right-2 bg-[#FFFFFF] text-primaryButton px-2 py-1 rounded-[20px] text-sm 
        font-medium h-[32px] min-w-[90px] flex items-center gap-1 justify-center shadow-2xl">
            <span>{store.city}</span>
            <MapPin size={16}/>
        </span>

        {/* Top Left: Open/Closed */}
        <div className="absolute top-2 left-2 flex gap-1 items-center">
            <span className={`px-2 py-1 font-semibold rounded text-[12px] text-white ${isOpen ? "bg-primaryButton" : "bg-Red"}`}>
            {isOpen ? t('open') : t("close")}
            </span>
            {isOpen && <span className=" text-white font-semibold text-[14px]">{store.openTime&&formatTime(store.openTime.slice(0,5))} {t("to")} {store.closeTime&&formatTime(store.closeTime.slice(0,5))}</span>}
        </div>


       <PermissionGate permissions={[PermissionsKey.ChangeStoreStatus]}>
        <div className=" absolute bottom-2 left-2 flex gap-2">
         {/* Bottom Right: Edit Icon */}
         {
          status ===StoreStatus.PENDING && 
          <div className="flex gap-2 justify-between">
            <button 
            onClick={handleApprove}
            className=" bg-primaryButton text-white text-[14px] capitalize p-1 rounded cursor-pointer hover:bg-opacity-85 transition">
              {t('approve')}
            </button>
            <button 
            onClick={handleReject}
            className=" bg-Red text-white text-[14px] capitalize p-1 rounded cursor-pointer hover:bg-opacity-85 transition">
              {t('reject')}
            </button>
          </div>
         }
        {
        status === StoreStatus.REJECTED &&
        <button 
            onClick={handleApprove}
            className=" bg-primaryButton text-white text-[14px] capitalize p-1 rounded cursor-pointer hover:bg-opacity-85 transition">
              {t('approve')}
            </button>
        }
       </div>
       </PermissionGate>
      </div>

      {/* Info Section */}
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-3">
            {/* logo */}
            {
              store.logoUrl
              ?
              <img src={store.logoUrl} alt="Logo" className="w-12 h-12 object-cover rounded-full" />
              :
              <div className="w-12 h-12 rounded-full shadow-2xl border-4 border-white bg-primary flex items-center justify-center text-white text-3xl font-bold z-10">
              {store.languages[0].name
                .split(" ")
                .map((w) => w[0])
                .join("")
                .toUpperCase()}
            </div>
            }
            <div className="flex-1">
            {/* Store name + rating */}
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-black text-Black capitalize">{storeName}</h2>
                    <span className="text-sm text-DarkGrey3 flex items-center">
                        <StarIcon className=" text-DarkGrey3" height={14.67} width={14.67}/> {formatNumber(store.averageRating)}
                        <span className=" ms-1 text-DarkGrey1">({formatNumber(store.numberOfRates)})</span>
                    </span>
                </div>
                {/* SubType + ParentType */}
                <div className="flex gap-2 text-sm text-Black capitalize items-center mt-1">
                    <div className="flex gap-1 items-center">
                        <img src={store.subType.type.iconUrl} alt={subTypeName} className="w-4 h-4"/>
                        <span>{subTypeName}</span>
                    </div>
                    <span className="w-[6px] h-[6px] rounded-full bg-DarkGrey1"></span>
                    <span>{parentTypeName}</span>
                </div>
                {/* Full Address */}
                <div className="text-sm text-Black mt-3 flex items-center gap-2">
                    <MapPin size={14}/>
                    <span>{address}</span>
                </div>
                <div className="mt-3 capitalize text-Black underline">
                  <Link to={`/admin/stores/${store.id}`}>{t("view")}</Link>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default StoreAdminBox;
